import { UserRoleE } from "./../../../models/enums";
import { selectDeliveryWizardData } from "./../../../store/selectors/deliveryWizardSelector";
import {
  selectValue,
  fileTypeSO,
  fileTypeHebrewSO,
} from "./../../../utils/helpers/selectOptions";
import { useAppDispatch, useAppSelector } from "./../../../store/hooks";
import { useState } from "react";
import { SelectOptionT, VersionT } from "../../../models/models";
import {
  addEmptyVersion,
  removerVersionByIndex,
  saveVersion,
  setDeliveryWizardStep,
  setEditableVersionIndex,
  setHouseNumber as setHouseNumberAction,
} from "../../../store/slices/deliveryWizardSlice";
import { DEFAULT_VERSION_NAME } from "../../../utils/constants";
import { useGetProjectCreationInfoQuery } from "../../../api/projectsApi";
import { selectCurrentLang } from "../../../store/selectors/appSelectors";
import { LangE } from "../../../models/enums";
import { ALL_WHITE_SPACE_REG_EXP } from "../../../utils/regexps";
import { selectCurrentUser } from "../../../store/selectors/authSelectors";
import buildVersionAdId from "./buildVersionAdId";

export const useVersionState = (defaultData: VersionT) => {
  const user = useAppSelector(selectCurrentUser);
  const lang = useAppSelector(selectCurrentLang);
  const dispatch = useAppDispatch();
  const versions = (useAppSelector(selectDeliveryWizardData) as any)?.delivery
    ?.Versions;
  const [name, setName] = useState(
    defaultData.Name === DEFAULT_VERSION_NAME ? "" : defaultData.Name || ""
  );
  const [requiredLength, setRequiredLength] = useState<number | undefined>(
    defaultData.RequiredLength || undefined
  );
  const [selectedOption, setSelectedOption] = useState<SelectOptionT>(
    selectValue(
      lang === LangE.EN ? fileTypeSO : fileTypeHebrewSO,
      +defaultData.FileType
    )
  );
  const [selectedSecondOption, setSelectedSecondOption] =
    useState<SelectOptionT>(
      selectValue(
        lang === LangE.EN ? fileTypeSO : fileTypeHebrewSO,
        +defaultData.SecondHouseNumberType
      )
    );
  const [isChannel5or9, setIsChannel5or9] = useState(defaultData.isChannel5or9);
  const [useFor2Spots, setUseFor2Spots] = useState(defaultData.useFor2Spots);
  const { data: { AdvertisingAgencyAdId, ExpectedProjectNumber } = {} } =
    useGetProjectCreationInfoQuery({});
  const [selectError, setSelectError] = useState(false);
  const [requiredLengthError, setRequiredLengthError] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [houseNumber, setHouseNumber] = useState(defaultData?.HouseNumber);
  const [sponsorshipHouseNumber, setSponsorshipHouseNumber] = useState(
    defaultData?.sponsershipHouseNumber
  );

  const _validate = () => {
    if (!name || !ALL_WHITE_SPACE_REG_EXP.test(name)) setNameError(true);
    if (!requiredLength) setRequiredLengthError(true);
    if (!selectedOption) setSelectError(true);
  };

  const denyChanges = (amountOfVersions: number, versionIndex: number) => {
    _validate();

    // if (!defaultData && (!name || !requiredLength || !selectedOption) && versionIndex === 0) return true;

    // of no input data and it is not the only version, remove the version
    if (
      !name &&
      !ALL_WHITE_SPACE_REG_EXP.test(name) &&
      !requiredLength &&
      !selectedOption &&
      amountOfVersions !== 1
    ) {
      dispatch(removerVersionByIndex(versionIndex));
      dispatch(setEditableVersionIndex(-1));
    } else if (defaultData.Name) {
      dispatch(setEditableVersionIndex(-1));
      setName(defaultData.Name);
      setRequiredLength(defaultData.RequiredLength);
      setSelectedOption(
        selectValue(
          lang === LangE.EN ? fileTypeSO : fileTypeHebrewSO,
          +defaultData?.FileType
        )
      );
      setSelectedSecondOption(
        selectValue(
          lang === LangE.EN ? fileTypeSO : fileTypeHebrewSO,
          +defaultData?.SecondHouseNumberType
        )
      );
      setIsChannel5or9(defaultData.isChannel5or9);
      setUseFor2Spots(defaultData.useFor2Spots);

      if (user.role === UserRoleE.Broadcaster) {
        setHouseNumber(defaultData.HouseNumber);
        setSponsorshipHouseNumber(defaultData.sponsershipHouseNumber);
      }
    }
  };

  const saveChanges = (withAddingEmpty: boolean, goToNextStep: boolean) => {
    _validate();

    if (name && requiredLength > 0 && selectedOption?.value) {
      const versionAdId = buildVersionAdId(
        AdvertisingAgencyAdId,
        ExpectedProjectNumber,
        requiredLength
      );
      const sameAdIdVersionsCount = versions?.filter((v) =>
        v.AdId.includes(versionAdId)
      ).length;

      dispatch(
        saveVersion({
          Name: name.trim(),
          RequiredLength: requiredLength,
          FileType: selectedOption.value,
          SecondHouseNumberType: user.role === UserRoleE.Broadcaster && useFor2Spots ? selectedSecondOption?.value : undefined,
          isChannel5or9,
          useFor2Spots,
          AdId: sameAdIdVersionsCount
            ? `${versionAdId}-${sameAdIdVersionsCount}`
            : versionAdId,
          HouseNumber:
            user.role === UserRoleE.Broadcaster ? houseNumber : undefined,
          sponsershipHouseNumber:
            user.role === UserRoleE.Broadcaster && useFor2Spots
              ? sponsorshipHouseNumber
              : undefined,
        })
      );
      if (withAddingEmpty) {
        dispatch(addEmptyVersion());
        setName("");
        setRequiredLength(undefined);
      }
      if (goToNextStep) {
        dispatch(setDeliveryWizardStep(3));
      }
    }
  };

  const startEdit = (versionIndex: number) => {
    dispatch(setEditableVersionIndex(versionIndex));
  };

  const removeVersion = (versionIndex: number) => {
    dispatch(removerVersionByIndex(versionIndex));
  };

  return {
    name,
    setName,
    requiredLength,
    setRequiredLength,
    selectedOption,
    setSelectedOption,
    selectedSecondOption,
    setSelectedSecondOption,
    isChannel5or9,
    setIsChannel5or9,
    useFor2Spots,
    setUseFor2Spots,
    selectError,
    setSelectError,
    nameError,
    setNameError,
    requiredLengthError,
    setRequiredLengthError,
    denyChanges,
    saveChanges,
    startEdit,
    removeVersion,
    houseNumber,
    setHouseNumber,
    sponsorshipHouseNumber,
    setSponsorshipHouseNumber,
  };
};
